// src/services/invoice.service.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Invoice,
  InvoiceLineItem,
  InvoiceStatus,
  CreateInvoiceDto,
  UpdateInvoiceDto,
} from '../types/finance.types';
import { getNextInvoiceNumber, getCompanySettings } from './companySettings.service';

const COLLECTION_NAME = 'invoices';

const generateId = () => `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function calculateTotals(lineItems: Omit<InvoiceLineItem, 'id'>[], taxRate?: number) {
  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxAmount = taxRate ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;
  return { subtotal, taxAmount, total };
}

export async function createInvoice(
  data: CreateInvoiceDto,
  userId: string
): Promise<Invoice> {
  try {
    // Get invoice number
    const invoiceNumber = await getNextInvoiceNumber(userId);

    // Process line items with IDs
    const lineItems: InvoiceLineItem[] = data.lineItems.map((item) => ({
      ...item,
      id: generateId(),
      amount: item.quantity * item.unitPrice,
    }));

    // Calculate totals
    const { subtotal, taxAmount, total } = calculateTotals(data.lineItems, data.taxRate);

    // Determine dates
    const issueDate = data.issueDate
      ? Timestamp.fromDate(data.issueDate)
      : Timestamp.now();

    const dueDate = data.dueDate
      ? Timestamp.fromDate(data.dueDate)
      : Timestamp.fromDate(
          new Date(issueDate.toDate().getTime() + data.paymentTerms * 24 * 60 * 60 * 1000)
        );

    const invoiceId = generateId();
    const invoice: Invoice = {
      id: invoiceId,
      invoiceNumber,
      organizationId: data.organizationId,
      organizationName: data.organizationName,
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      clientAddress: data.clientAddress,
      clientCity: data.clientCity,
      clientState: data.clientState,
      clientZip: data.clientZip,
      projectId: data.projectId,
      projectName: data.projectName,
      billingType: data.billingType,
      lineItems,
      billingDetail: data.billingDetail,
      subtotal,
      taxRate: data.taxRate ?? 0,
      taxAmount,
      total,
      status: 'draft',
      issueDate,
      dueDate,
      paymentTerms: data.paymentTerms,
      notes: data.notes ?? '',
      createdAt: Timestamp.now(),
      createdBy: userId,
      updatedAt: Timestamp.now(),
    };

    // Firestore rejects undefined anywhere (e.g. optional clientEmail/address
    // not supplied by the billing path). Drop undefined keys before writing.
    const cleanInvoice = Object.fromEntries(
      Object.entries(invoice).filter(([, v]) => v !== undefined)
    ) as Invoice;

    await setDoc(doc(db, COLLECTION_NAME, invoiceId), cleanInvoice);
    return invoice;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
}

export async function getInvoiceById(invoiceId: string): Promise<Invoice | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, invoiceId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as Invoice;
    }
    return null;
  } catch (error) {
    console.error('Error getting invoice:', error);
    throw error;
  }
}

export async function getInvoices(filters?: {
  organizationId?: string;
  projectId?: string;
  status?: InvoiceStatus;
  startDate?: Date;
  endDate?: Date;
}): Promise<Invoice[]> {
  try {
    let q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));

    if (filters?.organizationId) {
      q = query(q, where('organizationId', '==', filters.organizationId));
    }
    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }
    if (filters?.status) {
      q = query(q, where('status', '==', filters.status));
    }

    const snapshot = await getDocs(q);
    let invoices = snapshot.docs.map((doc) => doc.data() as Invoice);

    // Client-side date filtering
    if (filters?.startDate) {
      invoices = invoices.filter(
        (inv) => inv.issueDate.toDate() >= filters.startDate!
      );
    }
    if (filters?.endDate) {
      invoices = invoices.filter(
        (inv) => inv.issueDate.toDate() <= filters.endDate!
      );
    }

    return invoices;
  } catch (error) {
    console.error('Error getting invoices:', error);
    throw error;
  }
}

export async function updateInvoice(
  invoiceId: string,
  data: UpdateInvoiceDto
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, invoiceId);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    // Copy simple fields
    if (data.clientName !== undefined) updateData.clientName = data.clientName;
    if (data.clientEmail !== undefined) updateData.clientEmail = data.clientEmail;
    if (data.clientAddress !== undefined) updateData.clientAddress = data.clientAddress;
    if (data.clientCity !== undefined) updateData.clientCity = data.clientCity;
    if (data.clientState !== undefined) updateData.clientState = data.clientState;
    if (data.clientZip !== undefined) updateData.clientZip = data.clientZip;
    if (data.paymentTerms !== undefined) updateData.paymentTerms = data.paymentTerms;
    if (data.notes !== undefined) updateData.notes = data.notes;
    if (data.status !== undefined) updateData.status = data.status;

    // Handle dates
    if (data.issueDate) {
      updateData.issueDate = Timestamp.fromDate(data.issueDate);
    }
    if (data.dueDate) {
      updateData.dueDate = Timestamp.fromDate(data.dueDate);
    }
    if (data.paidDate) {
      updateData.paidDate = Timestamp.fromDate(data.paidDate);
    }

    // Handle line items and recalculate totals
    if (data.lineItems) {
      const lineItems: InvoiceLineItem[] = data.lineItems.map((item) => ({
        ...item,
        id: generateId(),
        amount: item.quantity * item.unitPrice,
      }));
      updateData.lineItems = lineItems;

      const taxRate = data.taxRate !== undefined ? data.taxRate : undefined;
      if (taxRate !== undefined) {
        updateData.taxRate = taxRate;
      }

      // Get current invoice for tax rate if not provided
      const current = await getInvoiceById(invoiceId);
      const effectiveTaxRate = taxRate ?? current?.taxRate;

      const { subtotal, taxAmount, total } = calculateTotals(data.lineItems, effectiveTaxRate);
      updateData.subtotal = subtotal;
      updateData.taxAmount = taxAmount;
      updateData.total = total;
    } else if (data.taxRate !== undefined) {
      // Just updating tax rate
      const current = await getInvoiceById(invoiceId);
      if (current) {
        updateData.taxRate = data.taxRate;
        const { subtotal, taxAmount, total } = calculateTotals(
          current.lineItems,
          data.taxRate
        );
        updateData.subtotal = subtotal;
        updateData.taxAmount = taxAmount;
        updateData.total = total;
      }
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
}

export async function updateInvoiceStatus(
  invoiceId: string,
  status: InvoiceStatus,
  paidDate?: Date
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, invoiceId);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };

    if (status === 'paid' && paidDate) {
      updateData.paidDate = Timestamp.fromDate(paidDate);
    } else if (status === 'paid') {
      updateData.paidDate = Timestamp.now();
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating invoice status:', error);
    throw error;
  }
}

export async function deleteInvoice(invoiceId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, invoiceId));
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
}

export async function updateOverdueInvoices(): Promise<number> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', 'sent')
    );

    const snapshot = await getDocs(q);
    const now = new Date();
    let updatedCount = 0;

    for (const docSnap of snapshot.docs) {
      const invoice = docSnap.data() as Invoice;
      if (invoice.dueDate.toDate() < now) {
        await updateDoc(docSnap.ref, {
          status: 'overdue',
          updatedAt: Timestamp.now(),
        });
        updatedCount++;
      }
    }

    return updatedCount;
  } catch (error) {
    console.error('Error updating overdue invoices:', error);
    throw error;
  }
}

export async function getInvoiceSummary(filters?: {
  organizationId?: string;
  projectId?: string;
  year?: number;
}): Promise<{
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  invoiceCount: number;
}> {
  try {
    const invoices = await getInvoices({
      organizationId: filters?.organizationId,
      projectId: filters?.projectId,
    });

    // Filter by year if specified
    let filteredInvoices = invoices;
    if (filters?.year) {
      filteredInvoices = invoices.filter(
        (inv) => inv.issueDate.toDate().getFullYear() === filters.year
      );
    }

    const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);
    const totalPaid = filteredInvoices
      .filter((inv) => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0);
    const totalOutstanding = filteredInvoices
      .filter((inv) => inv.status === 'sent' || inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.total, 0);
    const totalOverdue = filteredInvoices
      .filter((inv) => inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.total, 0);

    return {
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      totalOverdue,
      invoiceCount: filteredInvoices.length,
    };
  } catch (error) {
    console.error('Error getting invoice summary:', error);
    throw error;
  }
}

export async function duplicateInvoice(
  invoiceId: string,
  userId: string
): Promise<Invoice> {
  try {
    const original = await getInvoiceById(invoiceId);
    if (!original) {
      throw new Error('Invoice not found');
    }

    // Create new invoice with same data
    const newInvoice = await createInvoice(
      {
        organizationId: original.organizationId,
        organizationName: original.organizationName,
        clientName: original.clientName,
        clientEmail: original.clientEmail,
        clientAddress: original.clientAddress,
        clientCity: original.clientCity,
        clientState: original.clientState,
        clientZip: original.clientZip,
        projectId: original.projectId,
        projectName: original.projectName,
        billingType: original.billingType,
        lineItems: original.lineItems.map(({ id, ...rest }) => rest),
        taxRate: original.taxRate,
        paymentTerms: original.paymentTerms,
        notes: original.notes,
      },
      userId
    );

    return newInvoice;
  } catch (error) {
    console.error('Error duplicating invoice:', error);
    throw error;
  }
}

// Create invoice from billing period data
export async function createInvoiceFromBilling(
  billingPeriod: {
    projectId: string;
    projectName: string;
    periodStart: Date;
    periodEnd: Date;
    summary: {
      validLeads?: number;
      validStudents?: number;
      totalBillable?: number;
    };
    // Per-lead detail (for the invoice's page-2 breakdown) and cross-week
    // determination carry-over adjustments.
    leads?: Array<{ leadId?: string; parentName?: string; children?: any[]; currentDetermination?: string; billableAmount?: number }>;
    adjustments?: Array<{ leadId?: string; parentName?: string; kind: 'carryover' | 'credit'; students: number }>;
  },
  organizationId: string,
  organizationName: string,
  clientInfo: {
    clientName: string;
    clientEmail?: string;
    clientAddress?: string;
    clientCity?: string;
    clientState?: string;
    clientZip?: string;
  },
  ratePerLead: number,
  userId: string
): Promise<Invoice> {
  try {
    // Format billing period for display
    const periodLabel = `${billingPeriod.periodStart.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })} - ${billingPeriod.periodEnd.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`;

    // Base billable = valid students gathered this week.
    const billableUnits = billingPeriod.summary.validStudents ||
                          billingPeriod.summary.validLeads ||
                          billingPeriod.summary.totalBillable || 0;

    // Base line item.
    const lineItems: Omit<InvoiceLineItem, 'id'>[] = [
      {
        description: `Valid leads - ${billingPeriod.projectName} (${periodLabel})`,
        quantity: billableUnits,
        unitPrice: ratePerLead,
        amount: billableUnits * ratePerLead,
      }
    ];

    // Carry-over adjustments: prior-week leads whose determination changed this
    // week. carryover = +students billed now; credit = −students.
    const adjustments = billingPeriod.adjustments || [];
    for (const adj of adjustments) {
      const signedQty = adj.kind === 'credit' ? -adj.students : adj.students;
      lineItems.push({
        description: adj.kind === 'credit'
          ? `Credit — ${adj.parentName || 'Lead'} (determination reverted)`
          : `Carry-over — ${adj.parentName || 'Lead'} (validated this week)`,
        quantity: signedQty,
        unitPrice: ratePerLead,
        amount: signedQty * ratePerLead,
      });
    }

    // Per-lead detail for the invoice page-2 breakdown.
    const detailLeads = (billingPeriod.leads || []).map((l) => {
      const students = l.children?.length || 0;
      const det = l.currentDetermination || 'pending';
      return {
        parentName: l.parentName || 'Lead',
        students,
        determination: det,
        amount: det === 'valid' ? students * ratePerLead : 0,
      };
    });
    const detailAdjustments = adjustments.map((adj) => ({
      parentName: adj.parentName || 'Lead',
      kind: adj.kind,
      students: adj.students,
      amount: (adj.kind === 'credit' ? -adj.students : adj.students) * ratePerLead,
    }));
    const billingDetail = (detailLeads.length || detailAdjustments.length)
      ? { leads: detailLeads, adjustments: detailAdjustments }
      : undefined;

    const adjUnits = adjustments.reduce((s, a) => s + (a.kind === 'credit' ? -a.students : a.students), 0);

    // Get company settings for payment terms
    const settings = await getCompanySettings(organizationId);
    const paymentTerms = settings?.invoicePaymentTerms || 30;

    // Create the invoice
    const invoice = await createInvoice(
      {
        organizationId,
        organizationName,
        clientName: clientInfo.clientName,
        clientEmail: clientInfo.clientEmail,
        clientAddress: clientInfo.clientAddress,
        clientCity: clientInfo.clientCity,
        clientState: clientInfo.clientState,
        clientZip: clientInfo.clientZip,
        projectId: billingPeriod.projectId,
        projectName: billingPeriod.projectName,
        billingType: 'per_lead',
        lineItems,
        billingDetail,
        paymentTerms,
        notes: `Billing period: ${periodLabel}\nValid students this week: ${billableUnits}` +
          (adjUnits ? `\nCarry-over adjustments: ${adjUnits > 0 ? '+' : ''}${adjUnits} student(s)` : '')
      },
      userId
    );

    return invoice;
  } catch (error) {
    console.error('Error creating invoice from billing:', error);
    throw error;
  }
}

export const invoiceService = {
  createInvoice,
  createInvoiceFromBilling,
  getInvoiceById,
  getInvoices,
  updateInvoice,
  updateInvoiceStatus,
  deleteInvoice,
  updateOverdueInvoices,
  getInvoiceSummary,
  duplicateInvoice,
};
